import { useState } from 'react';
import { Pagination, Popconfirm } from 'antd';

import { cashKey } from '@/api/cash';
import { useVendors } from '@/api/masters';
import { outstandingKey } from '@/api/outstanding';
import { useApiMutation } from '@/api/mutations';
import { purchaseOrdersKey } from '@/api/purchaseOrders';
import { purchaseBillsKey } from '@/api/purchaseBills';
import {
  cancelVendorPayment,
  createVendorPayment,
  useVendorLedger,
  useVendorPayment,
  useVendorPaymentAccounts,
  useVendorPayments,
  vendorPaymentsKey,
  type VendorPayment,
  type VendorPaymentSummary,
} from '@/api/vendorPayments';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { DatePicker } from '@/components/DatePicker';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import {
  DocumentForm,
  DocumentFormActions,
  DocumentFormFoot,
  DocumentFormHead,
  DocumentHeading,
  DocumentLines,
  DocumentPaper,
  DocumentParties,
  DocumentTop,
  DocumentTotals,
  FormSectionTitle,
  LineName,
  type DocumentColumn,
} from '@/components/DocumentSheet';
import { Field, controlClass, selectClass, textAreaClass } from '@/components/Field';
import { IconAction } from '@/components/IconAction';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import { StatusBadge } from '@/components/StatusBadge';
import type { PaymentMethod } from '@core/generated/enums';
import { paymentMethodLabel } from '@core/bookings/paymentWords';
import { formatDocumentDate, toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import {
  accountsFor,
  defaultPaidFrom,
  paidFromAfter,
  paidFromLabel,
  paidFromProblem,
} from '@core/cash/paidFrom';
import {
  chosenTotal,
  dueKey,
  emptyPayment,
  oldestFirst,
  paymentMethods,
  paymentProblems,
  toPaymentRequest,
  type PaymentDraft,
} from '@core/purchasing/payment';
import { canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';

const PAGE_SIZE = 25;

type Screen = { kind: 'list' } | { kind: 'new' } | { kind: 'view'; id: number };

const rupees = (value: number) => `₹${formatAmount(value)}`;

/**
 * Commerce › Vendor payments (step 4 of `docs/Purchase_Sale_Outstanding_Design.md`) — money paid to
 * suppliers for their bills and to launderers and tailors for finished jobs. A payment is set
 * against what is owed oldest first, or against amounts the shop chooses; what is over stays on
 * account. It posts to the cash book as money out, and cancelling reverses it today.
 */
/**
 * @param payVendorId Opens straight on Pay a vendor with this vendor chosen — Outstanding's Pay.
 */
export function VendorPaymentsPage({
  session,
  payVendorId,
  startNew = false,
  openId,
  onExit,
}: {
  session: Session;
  payVendorId?: number | undefined;
  /** Opens on Pay a vendor with no vendor chosen yet — Money out on the cash board. */
  startNew?: boolean;
  /** Opens on this one — the purchase desk's history. */
  openId?: number | undefined;
  /** Where Back from it goes instead of the list — the desk. */
  onExit?: (() => void) | undefined;
}) {
  const [screen, setScreen] = useState<Screen>(() =>
    openId !== undefined
      ? { kind: 'view', id: openId }
      : payVendorId === undefined && !startNew
        ? { kind: 'list' }
        : { kind: 'new' },
  );
  const toList = () => (onExit ? onExit() : setScreen({ kind: 'list' }));
  const [today] = useState(() => toIsoDate(new Date()) ?? '');
  const payment = useVendorPayment(screen.kind === 'view' ? screen.id : null);

  if (screen.kind === 'new') {
    return (
      <PaymentEditor
        session={session}
        today={today}
        vendorId={payVendorId}
        onClose={() => toList()}
        onSaved={(saved) => setScreen({ kind: 'view', id: saved.id })}
      />
    );
  }

  if (screen.kind === 'view') {
    return payment.data ? (
      <PaymentView session={session} payment={payment.data} onBack={() => toList()} />
    ) : null;
  }

  return (
    <PaymentList
      session={session}
      onNew={() => setScreen({ kind: 'new' })}
      onOpen={(id) => setScreen({ kind: 'view', id })}
    />
  );
}

/* ------------------------------------------------------------------------------------------------ */

function PaymentList({
  session,
  onNew,
  onOpen,
}: {
  session: Session;
  onNew: () => void;
  onOpen: (id: number) => void;
}) {
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [page, setPage] = useState(1);
  const payments = useVendorPayments(session.branch?.id, {
    includeCancelled,
    page,
    pageSize: PAGE_SIZE,
  });
  const total = payments.data?.total ?? 0;

  const columns: DataViewColumn<VendorPaymentSummary>[] = [
    {
      title: 'Payment',
      dataIndex: 'documentNumber',
      primary: true,
      render: (_, row) => (
        <div>
          <div className="font-mono-nums text-sm font-medium text-gray-900">
            {row.documentNumber}
          </div>
          <div className="text-default text-[12px]">{formatDocumentDate(row.paidOn)}</div>
        </div>
      ),
    },
    {
      title: 'Paid to',
      dataIndex: 'vendorName',
      render: (_, row) => (
        <div>
          <div className="text-dark text-[13px]">{row.vendorName}</div>
          <div className="text-default text-[11px]">{paymentMethodLabel[row.method]}</div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (_, row) => (
        <span className="font-mono-nums text-dark text-[13px]">
          {row.cancelled ? <s>{formatAmount(row.amount)}</s> : formatAmount(row.amount)}
        </span>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      trailing: true,
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {row.cancelled && <StatusBadge tone="neutral">Cancelled</StatusBadge>}
          <IconAction
            icon="icon icon-chevron-right"
            label={`Open ${row.documentNumber}`}
            onClick={() => onOpen(row.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Vendor payments"
        subtitle="Money paid to suppliers, launderers and tailors — set against what each is owed, oldest first unless you choose."
        actions={
          canAct(session, 'VendorPayment:Create') ? (
            <Button variant="primary" icon="icon icon-plus" shortcut="create" onClick={onNew}>
              Pay a vendor
            </Button>
          ) : undefined
        }
      />

      <Card>
        <div className="border-border-color flex items-center gap-3 border-b pb-3">
          <label className="text-default inline-flex cursor-pointer items-center gap-2 text-[12px]">
            <input
              type="checkbox"
              checked={includeCancelled}
              onChange={(event) => {
                setIncludeCancelled(event.target.checked);
                setPage(1);
              }}
              className="accent-primary size-3.5 cursor-pointer"
            />
            Show cancelled
          </label>
        </div>

        <DataView<VendorPaymentSummary>
          columns={columns}
          dataSource={payments.data?.items ?? []}
          loading={payments.isLoading}
          empty={
            <EmptyState
              icon="icon icon-wallet"
              title="No vendor has been paid yet"
              hint="Pay a vendor sets the money against their bills and finished jobs, and posts it to the cash book."
            />
          }
        />

        {total > PAGE_SIZE && (
          <div className="border-border-color flex justify-end border-t py-3">
            <Pagination
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(count) => `${count} payments`}
            />
          </div>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------------------------------------ */

function PaymentEditor({
  session,
  today,
  vendorId,
  onClose,
  onSaved,
}: {
  session: Session;
  today: string;
  vendorId: number | undefined;
  onClose: () => void;
  onSaved: (payment: VendorPayment) => void;
}) {
  const [draft, setDraft] = useState<PaymentDraft>(() => ({
    ...emptyPayment(today),
    vendorId: vendorId ?? null,
  }));
  const [picked, setVendor] = useState<PickerOption | null>(null);
  const [search, setSearch] = useState('');
  const vendors = useVendors(session.branch?.id, { search, pageSize: 10 });
  const ledger = useVendorLedger(draft.vendorId);
  const accounts = useVendorPaymentAccounts(session.branch?.id);
  const choices = accountsFor(draft.method, accounts.data);
  const shownAccount = draft.cashAccountId ?? defaultPaidFrom(draft.method, accounts.data);
  // Arriving with a vendor already chosen, the picker shows the name the ledger carries.
  const vendor =
    picked ??
    (ledger.data && draft.vendorId === ledger.data.vendorId
      ? { id: String(ledger.data.vendorId), primary: ledger.data.vendorName }
      : null);
  const accountProblem = paidFromProblem(draft.cashAccountId, draft.method, accounts.data);
  const problems = [
    ...paymentProblems(draft, ledger.data),
    ...(accountProblem ? [accountProblem] : []),
  ];
  const preview = ledger.data ? oldestFirst(ledger.data, draft.amount ?? 0) : null;
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const save = useApiMutation<VendorPayment, PaymentDraft>({
    mutationFn: (input) => createVendorPayment(toPaymentRequest(input, ledger.data)),
    success: (saved) =>
      `${saved.documentNumber} — ${rupees(saved.amount)} paid to ${saved.vendorName}.`,
    invalidates: [vendorPaymentsKey, purchaseBillsKey, purchaseOrdersKey, cashKey, outstandingKey],
    onSuccess: onSaved,
  });
  const busy = save.isPending;

  type Due = NonNullable<typeof ledger.data>['dues'][number];

  const dueColumns: DocumentColumn<Due>[] = [
    {
      key: 'due',
      title: 'Owed for',
      render: (due) => (
        <LineName
          name={
            <>
              {due.description}{' '}
              <span className="text-default font-mono-nums font-normal">{due.reference}</span>
            </>
          }
          note={`${due.kind === 'Bill' ? 'Due' : 'Back'} ${formatDocumentDate(due.dueDate)}`}
        />
      ),
    },
    { key: 'total', title: 'Total', numeric: true, render: (due) => rupees(due.total) },
    {
      key: 'left',
      title: 'Left to pay',
      numeric: true,
      render: (due) => <span className="font-semibold">{rupees(due.outstanding)}</span>,
    },
    {
      key: 'pay',
      title: 'This payment',
      numeric: true,
      render: (due) => {
        const key = dueKey(due.kind, due.id);
        const auto = preview?.lines.find((line) => line.due === due)?.amount ?? 0;

        return draft.choosing ? (
          <input
            type="number"
            onWheel={(event) => event.currentTarget.blur()}
            min={0}
            max={due.outstanding}
            step="0.01"
            aria-label={`Pay against ${due.reference}`}
            value={draft.chosen[key] ?? ''}
            disabled={busy}
            onChange={(event) =>
              setDraft((was) => ({
                ...was,
                chosen: { ...was.chosen, [key]: Number(event.target.value) || 0 },
              }))
            }
            className={`${controlClass} max-w-32 text-right`}
          />
        ) : (
          <span className="font-semibold">{auto > 0 ? rupees(auto) : '—'}</span>
        );
      },
    },
  ];

  return (
    <>
      <DocumentHeading
        trail="Vendor payments"
        current="New"
        title="Pay a vendor"
        onBack={onClose}
      />

      <div ref={fields} {...enterKeyScope}>
        <DocumentForm>
          <DocumentFormHead
            left={
              <>
                <FormSectionTitle>Vendor</FormSectionTitle>
                <Field label="Paid to">
                  <SearchPicker
                    value={vendor}
                    options={(vendors.data?.items ?? []).map((option) => ({
                      id: String(option.id),
                      primary: option.name,
                      secondary: option.mobile,
                    }))}
                    total={vendors.data?.total ?? 0}
                    loading={vendors.isLoading}
                    disabled={busy}
                    placeholder="Search suppliers, launderers, tailors"
                    onSearch={setSearch}
                    onSelect={(option) => {
                      setVendor(option);
                      setDraft((was) => ({
                        ...was,
                        vendorId: option === null ? null : Number(option.id),
                        chosen: {},
                      }));
                    }}
                  />
                </Field>
                {ledger.data && (
                  <div className="text-[length:var(--scale-font-size)]">
                    <p className="flex justify-between py-0.5">
                      <span className="text-default">
                        Owed
                        {ledger.data.isMsme && (
                          <span className="ms-2">
                            <StatusBadge tone="neutral">MSME</StatusBadge>
                          </span>
                        )}
                      </span>
                      <span className="font-mono-nums font-semibold">
                        {rupees(ledger.data.owed)}
                      </span>
                    </p>
                    <p className="flex justify-between py-0.5">
                      <span className="text-default">Already on account</span>
                      <span className="font-mono-nums font-semibold">
                        {rupees(ledger.data.onAccount)}
                      </span>
                    </p>
                  </div>
                )}
              </>
            }
            right={
              <>
                <FormSectionTitle>Payment details</FormSectionTitle>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Amount paid (₹)">
                    <input
                      type="number"
                      onWheel={(event) => event.currentTarget.blur()}
                      min={0}
                      step="0.01"
                      value={draft.amount ?? ''}
                      disabled={busy}
                      onChange={(event) =>
                        setDraft((was) => ({
                          ...was,
                          amount: event.target.value === '' ? null : Number(event.target.value),
                        }))
                      }
                      className={controlClass}
                    />
                  </Field>
                  <Field label="Paid on">
                    <DatePicker
                      className="w-full"
                      value={toDayjs(draft.paidOn) ?? null}
                      disabled={busy}
                      onChange={(value) =>
                        setDraft((was) => ({ ...was, paidOn: toIsoDate(value) ?? was.paidOn }))
                      }
                    />
                  </Field>
                  <Field label="How">
                    <select
                      value={draft.method}
                      disabled={busy}
                      onChange={(event) => {
                        const method = event.target.value as PaymentMethod;

                        setDraft((was) => ({
                          ...was,
                          method,
                          cashAccountId:
                            was.cashAccountId === null
                              ? null
                              : paidFromAfter(was.cashAccountId, method, accounts.data),
                        }));
                      }}
                      className={selectClass}
                    >
                      {paymentMethods.map((method) => (
                        <option key={method} value={method}>
                          {paymentMethodLabel[method]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    label="Paid from"
                    hint={
                      choices.length === 0
                        ? draft.method === 'Cash'
                          ? 'The branch’s till, set up the first time money moves.'
                          : 'The branch’s bank account, set up the first time money moves.'
                        : undefined
                    }
                  >
                    {choices.length > 0 && (
                      <select
                        value={shownAccount ?? ''}
                        disabled={busy}
                        onChange={(event) =>
                          setDraft((was) => ({ ...was, cashAccountId: Number(event.target.value) }))
                        }
                        className={selectClass}
                      >
                        {shownAccount === null && <option value="">Choose an account</option>}
                        {choices.map((account) => (
                          <option key={account.id} value={account.id}>
                            {paidFromLabel(account)}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                  <Field label="Reference" hint="Cheque number, UTR, transaction id.">
                    <input
                      value={draft.reference}
                      disabled={busy}
                      onChange={(event) =>
                        setDraft((was) => ({ ...was, reference: event.target.value }))
                      }
                      className={controlClass}
                    />
                  </Field>
                </div>
              </>
            }
          />

          {ledger.data && (
            <>
              <FormSectionTitle
                aside={
                  <label className="text-default inline-flex cursor-pointer items-center gap-2 text-[length:var(--scale-list-meta)]">
                    <input
                      type="checkbox"
                      checked={draft.choosing}
                      disabled={busy}
                      onChange={(event) =>
                        setDraft((was) => ({ ...was, choosing: event.target.checked, chosen: {} }))
                      }
                      className="accent-primary size-3.5 cursor-pointer"
                    />
                    Choose the amounts myself
                  </label>
                }
              >
                What they are owed
              </FormSectionTitle>

              <DocumentLines
                columns={dueColumns}
                rows={ledger.data.dues}
                rowKey={(due) => dueKey(due.kind, due.id)}
                empty={`Nothing is owed to ${ledger.data.vendorName} at this branch. A payment now stays on account against their next bill or job.`}
              />

              {ledger.data.uncostedJobs.length > 0 && (
                <p className="text-default mb-6 text-[length:var(--scale-list-meta)]">
                  {ledger.data.uncostedJobs.length} finished job
                  {ledger.data.uncostedJobs.length === 1 ? ' has' : 's have'} no cost recorded, so
                  nothing is shown as owed for{' '}
                  {ledger.data.uncostedJobs.length === 1 ? 'it' : 'them'}:{' '}
                  {ledger.data.uncostedJobs
                    .map((job) => job.documentNumber || 'one with no number')
                    .join(', ')}
                  .
                </p>
              )}
            </>
          )}

          <DocumentFormFoot
            left={
              <Field label="Notes">
                <textarea
                  rows={3}
                  value={draft.notes}
                  disabled={busy}
                  onChange={(event) => setDraft((was) => ({ ...was, notes: event.target.value }))}
                  className={textAreaClass}
                />
              </Field>
            }
            right={
              <>
                {ledger.data && (
                  <DocumentTotals
                    rows={[
                      { label: 'Owed', value: rupees(ledger.data.owed) },
                      { label: 'Already on account', value: rupees(ledger.data.onAccount) },
                      ...(draft.amount !== null && draft.amount > 0
                        ? [
                            {
                              label: 'Left on account after this',
                              value: rupees(
                                draft.choosing
                                  ? Math.max(0, draft.amount - chosenTotal(draft)) +
                                      ledger.data.onAccount
                                  : (preview?.leftOnAccount ?? 0),
                              ),
                            },
                          ]
                        : []),
                      {
                        label: 'This payment',
                        value: rupees(draft.amount ?? 0),
                        grand: true,
                      },
                    ]}
                  />
                )}
                {problems.length > 0 && (
                  <Notice tone="warning" title="Not ready to save">
                    <ul className="mt-1 flex list-disc flex-col gap-0.5 ps-4">
                      {problems.slice(0, 6).map((problem) => (
                        <li key={problem}>{problem}</li>
                      ))}
                    </ul>
                  </Notice>
                )}
              </>
            }
          />

          <DocumentFormActions>
            <span className="text-default me-auto text-[length:var(--scale-list-meta)]">
              Posted to the cash book as money out, from the till for cash and the bank otherwise.
            </span>
            <Button variant="secondary" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              data-enter-key-commit
              shortcut="save"
              disabled={busy || problems.length > 0}
              onClick={() => void save.mutateAsync(draft).catch(() => undefined)}
            >
              {busy ? 'Saving…' : `Pay ${draft.amount ? rupees(draft.amount) : ''}`}
            </Button>
          </DocumentFormActions>
        </DocumentForm>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------------------------------ */

function PaymentView({
  session,
  payment,
  onBack,
}: {
  session: Session;
  payment: VendorPayment;
  onBack: () => void;
}) {
  const [reason, setReason] = useState('');
  const cancel = useApiMutation<VendorPayment, string>({
    mutationFn: (why) => cancelVendorPayment(payment.id, why),
    success: (saved) => `${saved.documentNumber} cancelled; the cash book is reversed.`,
    invalidates: [vendorPaymentsKey, purchaseBillsKey, purchaseOrdersKey, cashKey, outstandingKey],
  });

  type Allocation = VendorPayment['allocations'][number];

  const columns: DocumentColumn<Allocation>[] = [
    {
      key: 'against',
      title: 'Paid against',
      render: (allocation) => (
        <LineName
          name={<span className="font-mono-nums">{allocation.reference}</span>}
          note={allocation.kind === 'Bill' ? 'Supplier bill' : 'Finished job'}
        />
      ),
    },
    {
      key: 'amount',
      title: 'Amount',
      numeric: true,
      render: (allocation) => <span className="font-semibold">{rupees(allocation.amount)}</span>,
    },
  ];

  return (
    <>
      <DocumentHeading
        trail="Vendor payments"
        current={payment.documentNumber}
        title="Payment voucher"
        onBack={onBack}
        actions={
          !payment.cancelledOn && canAct(session, 'VendorPayment:Edit') ? (
            <Popconfirm
              title="Cancel this payment?"
              description={
                <div className="w-60 pt-1">
                  <p className="text-default mb-1.5 text-[12px]">
                    The cash book takes a reversing line today, and what it paid is owed again.
                  </p>
                  <input
                    type="text"
                    placeholder="Why?"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    className="border-border-color h-8 w-full rounded border bg-white px-2 text-[12px] text-gray-900"
                  />
                </div>
              }
              okText="Cancel payment"
              okButtonProps={{
                danger: true,
                loading: cancel.isPending,
                disabled: reason.trim().length === 0,
              }}
              cancelText="Keep it"
              onConfirm={() => {
                cancel.mutate(reason.trim());
                setReason('');
              }}
              placement="bottomRight"
            >
              <Button variant="danger" icon="icon icon-x" disabled={cancel.isPending}>
                Cancel payment
              </Button>
            </Popconfirm>
          ) : undefined
        }
      />

      {payment.cancelledOn && (
        <div className="mb-3">
          <Notice tone="info" title={`Cancelled on ${formatDocumentDate(payment.cancelledOn)}`}>
            {payment.cancelReason}
          </Notice>
        </div>
      )}

      <DocumentPaper>
        <DocumentTop
          issuer={session.branch?.name ?? 'This branch'}
          issuerLines={[]}
          title="Payment voucher"
          number={payment.documentNumber}
          status={
            payment.cancelledOn
              ? { label: 'Cancelled', tone: 'neutral' }
              : { label: 'Paid', tone: 'positive' }
          }
        />
        <DocumentParties
          heading="Paid to"
          name={payment.vendorName}
          lines={[]}
          facts={[
            { label: 'Paid on', value: formatDocumentDate(payment.paidOn) },
            { label: 'How', value: paymentMethodLabel[payment.method] },
            ...(payment.reference ? [{ label: 'Reference', value: payment.reference }] : []),
            ...(payment.cashAccountName
              ? [{ label: 'Paid from', value: payment.cashAccountName }]
              : []),
          ]}
        />
        <DocumentLines
          columns={columns}
          rows={payment.allocations}
          rowKey={(allocation) => `${allocation.kind}-${allocation.targetId}`}
          empty="Nothing — it is all on account."
        />
        <DocumentTotals
          rows={[
            { label: 'Set against bills and jobs', value: rupees(payment.allocated) },
            { label: 'On account', value: rupees(payment.onAccount) },
            { label: 'Paid', value: rupees(payment.amount), grand: true },
          ]}
        />
        {payment.notes && (
          <p className="text-default border-border-color mt-6 border-t pt-4 text-[length:var(--scale-list-meta)]">
            {payment.notes}
          </p>
        )}
      </DocumentPaper>
    </>
  );
}
